import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { FORM_OPTIONS, CONTACT } from '@/config/constants';
import { phoneSchema, formatPhoneNumber } from '@/lib/validation';

const contactFormSchema = z.object({
  name: z.string().trim().min(1, 'Enter your full name').max(100, 'Name must be less than 100 characters'),
  phone: phoneSchema,
  email: z
    .string()
    .trim()
    .min(1, 'Enter an email like name@example.com')
    .email('Enter an email like name@example.com')
    .max(255, 'Email must be less than 255 characters'),
  address: z.string().trim().min(1, 'Enter the property address').max(200, 'Address must be less than 200 characters'),
  service: z.string().min(1, 'Pick the service you need'),
  timeline: z.string().min(1, 'Pick a timeline'),
  details: z.string().trim().max(1000, 'Details must be less than 1000 characters').optional(),
  website: z.string().max(0, 'Invalid submission').optional(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const DEFAULT_TIMELINE = 'asap';

const defaultValues: ContactFormValues = {
  name: '',
  phone: '',
  email: '',
  address: '',
  service: '',
  timeline: DEFAULT_TIMELINE,
  details: '',
  website: '',
};

const selectClassName =
  'flex h-11 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-ring-smooth transition-all duration-300 hover:border-primary/50 cursor-pointer';

const inputClassName =
  'h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg';

export default function ContactForm() {
  const { t } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const onSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);

    // Check honeypot - if filled, silently reject
    if (values.website && values.website.length > 0) {
      logger.warn('ContactForm', 'Spam detected: honeypot field filled');
      setIsSubmitting(false);
      toast.error('There was an issue with your submission. Please try again.');
      return;
    }

    try {
      const formattedPhone = formatPhoneNumber(values.phone);
      // contacts.email is NOT NULL in the database; optional email is sent as an empty string
      const email = values.email ?? '';

      const { data: insertedData, error: dbError } = await supabase.from('contacts').insert({
        name: values.name,
        email,
        phone: formattedPhone,
        address: values.address,
        service: values.service,
        timeline: values.timeline,
        message: values.details || null,
      }).select('id').single();

      if (dbError) {
        if (dbError.message?.includes('row-level security policy')) {
          toast.error(
            `You've already submitted a request recently. Please wait 24 hours or call us directly at ${CONTACT.PHONE}.`
          );
          setIsSubmitting(false);
          return;
        }
        throw dbError;
      }

      // Sync to JobNimbus (which also sends Teams notification)
      // Fire and forget - don't block user
      if (insertedData?.id) {
        supabase.functions.invoke('sync-contact-to-jobnimbus', {
          body: {
            id: insertedData.id,
            name: values.name,
            email,
            phone: formattedPhone,
            address: values.address,
            service: values.service,
            timeline: values.timeline,
            message: values.details || null,
          },
        }).catch((err) => {
          logger.error('ContactForm', 'JobNimbus sync error (non-blocking)', { error: err });
        });
      }

      // Track Meta Pixel Lead event
      const w = window as unknown as { fbq?: (...args: unknown[]) => void };
      if (typeof window !== 'undefined' && w.fbq) {
        w.fbq('track', 'Lead', {
          content_name: 'Contact Form Submission',
          content_category: values.service,
          value: values.timeline,
        });
      }

      setSubmitted(true);
    } catch (error) {
      logger.error('ContactForm', 'Error submitting contact form', { error });
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    form.reset(defaultValues);
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div role="status" aria-live="polite" className="space-y-5">
        <Check className="h-8 w-8 text-primary" strokeWidth={2.5} aria-hidden="true" />
        <h3 className="font-heading text-2xl uppercase text-navy">We got it</h3>
        <p className="text-sm text-muted-foreground">
          We'll call you within the hour during business hours. Need it faster? Call{' '}
          <a href={CONTACT.PHONE_HREF} className="font-semibold text-navy underline underline-offset-4">
            {CONTACT.PHONE}
          </a>
          .
        </p>
        <div>
          <p className="text-sm font-medium text-charcoal mb-2">What to have ready</p>
          <ul className="bullet-list">
            <li>Roof age, if you know it</li>
            <li>Any insurance claim number</li>
            <li>Best time to reach you</li>
          </ul>
        </div>
        <Button type="button" variant="outline" size="lg" onClick={handleReset} className="w-full h-12 text-base">
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Phone Number *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder={CONTACT.PHONE} {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Property Address *</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, Houston, TX 77001" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">What brings you to us today? *</FormLabel>
              <FormControl>
                <select {...field} className={selectClassName}>
                  <option value="">Select a service...</option>
                  {FORM_OPTIONS.services.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="timeline"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">When do you need this done?</FormLabel>
              <FormControl>
                <select {...field} className={selectClassName}>
                  {FORM_OPTIONS.timelines.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Tell us more about your roof (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional details about your roofing needs..."
                  className="min-h-[100px] bg-white border-input focus-visible:ring-primary focus-ring-smooth transition-all duration-300"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot field - hidden from users, catches bots */}
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input {...field} tabIndex={-1} autoComplete="off" />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting}
          className="w-full bg-primary text-white hover:bg-primary/90 font-semibold h-12 text-base"
        >
          {isSubmitting ? 'Sending...' : t('cta.getFreeInspection')}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-2">
          Your information is secure. We'll contact you the same day, typically within the same hour.
        </p>
      </form>
    </Form>
  );
}
